import * as React from 'react';
import { toast } from '@grandvisionhq/common';
import { useIntl } from '@grandvisionhq/state';

const defaultSettings = {
    enableHighAccuracy: false,
    timeout: Infinity,
    maximumAge: 0
};
const useLocation = (watch = false, options)=>{
    const settings = Object.assign({}, defaultSettings, options);
    const intl = useIntl();
    const [position, setPosition] = React.useState(undefined);
    const [error1, setError] = React.useState(null);
    const [isUseMyLocation, setIsUseMyLocation] = React.useState(false);
    const [isMyLocationRequested, setIsMyLocationRequested] = React.useState(false);
    const [isPositionLoading, setIsPositionLoading] = React.useState(false);
    const [hasPermissionDeniedError, setHasPermissionDeniedError] = React.useState(false);
    const onChange = ({ coords , timestamp  })=>{
        setPosition({
            lat: coords.latitude,
            lng: coords.longitude,
            accuracy: coords.accuracy,
            timestamp
        });
        setIsPositionLoading(false);
    };
    const onError = (error)=>{
        setIsPositionLoading(false);
        setIsUseMyLocation(false);
        console.warn('Failed to get user coords:', error.message);
        switch(error.code){
            case error.PERMISSION_DENIED:
                setError(intl.getLabel('stores.yourLocation.permissionDenied'));
                toast.warning('stores.yourLocation.permissionDenied');
                break;
            default:
                setError(intl.getLabel('stores.yourLocation.error'));
                toast.warning('stores.yourLocation.error');
        }
    };
    const checkPermissionDeniedStatus = (state)=>{
        setHasPermissionDeniedError(state === 'denied');
    };
    React.useEffect(()=>{
        // Check first if geolocation permissions denied in the browser to show notification on button click.
        if ('permissions' in navigator) {
            navigator.permissions.query({
                name: 'geolocation'
            }).then((permissionStatus)=>{
                checkPermissionDeniedStatus(permissionStatus.state);
                // Check permissions if user change setting in the browser
                permissionStatus.addEventListener('change', ()=>{
                    checkPermissionDeniedStatus(permissionStatus.state);
                });
            });
        } else {
            // Internet Explorer, Safari, WebView Android and Safari on iOS do not support this feature by now,
            // set setHasPermissionDeniedError to false in this case.
            // In such browsers we will execute watchPosition or getCurrentPosition
            // to check and set correct value to hasPermissionDeniedError.
            setHasPermissionDeniedError(false);
        }
    }, []);
    React.useEffect(()=>{
        if (!isMyLocationRequested) {
            setIsUseMyLocation(false);
        }
        if (isMyLocationRequested) {
            setError(null);
            if (hasPermissionDeniedError) {
                setError(intl.getLabel('stores.yourLocation.permissionDenied'));
                toast.warning('stores.yourLocation.permissionDenied');
                setIsMyLocationRequested(false);
                setIsPositionLoading(false);
                return;
            }
            const hasBrowserSupport = typeof window !== 'undefined' && typeof window.navigator !== 'undefined' && typeof window.navigator.geolocation !== 'undefined';
            if (!hasBrowserSupport) {
                setError(intl.getLabel('stores.yourLocation.unsupported'));
                toast.warning('stores.yourLocation.unsupported');
                setIsMyLocationRequested(false);
                return;
            }
            setIsPositionLoading(true);
            setIsUseMyLocation(true);
            const geo = window.navigator.geolocation;
            let watcher = null;
            if (watch) {
                watcher = geo.watchPosition(onChange, onError, settings);
            } else {
                geo.getCurrentPosition(onChange, onError, settings);
            }
            return ()=>{
                watcher && geo.clearWatch(watcher);
            };
        }
    }, [
        isMyLocationRequested,
        hasPermissionDeniedError
    ]);
    return React.useMemo(()=>({
            position,
            isUseMyLocation,
            isPositionLoading,
            isMyLocationRequested,
            error: error1,
            setIsMyLocationRequested
        })
    , [
        position,
        isUseMyLocation,
        isPositionLoading,
        isMyLocationRequested,
        error1,
        setIsMyLocationRequested, 
    ]);
};

export { useLocation as default };
//# sourceMappingURL=use-location.js.map
